import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/components/ui/button';

/**
 * Everything under /p/* is a public page shown to patients, not staff. The
 * service worker still registers there (it serves the same app shell), but the
 * banner does not: "A new version of Synapse is available — Reload" means
 * nothing to a patient, and clicking it mid-form would throw away what they had
 * typed.
 */
function isPublicPath(pathname: string): boolean {
  return pathname === '/p' || pathname.startsWith('/p/');
}

export function PwaUpdater() {
  const { pathname } = useLocation();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(err) {
      console.error('SW registration failed', err);
    },
  });

  const [offlineDismissed, setOfflineDismissed] = useState(false);
  useEffect(() => {
    if (!offlineReady || offlineDismissed) return;
    const t = window.setTimeout(() => {
      setOfflineReady(false);
      setOfflineDismissed(true);
    }, 4000);
    return () => window.clearTimeout(t);
  }, [offlineReady, offlineDismissed, setOfflineReady]);

  if (isPublicPath(pathname)) return null;
  if (!needRefresh && !offlineReady) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4"
    >
      <div className="pointer-events-auto flex max-w-md items-center gap-3 rounded-lg border border-line-2 bg-paper px-4 py-3 text-sm text-ink shadow-lg">
        {needRefresh ? (
          <>
            <span className="flex-1">A new version of Synapse is available.</span>
            <Button size="sm" variant="primary" onClick={() => updateServiceWorker(true)}>
              Reload
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setNeedRefresh(false)}>
              Later
            </Button>
          </>
        ) : (
          <span className="flex-1">Ready to use offline.</span>
        )}
      </div>
    </div>
  );
}
